import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
    LayoutDashboard, Building2, Users, Activity, LogOut,
    Menu, X, Moon, Sun, Shield, ChevronLeft, ChevronRight, Target, CreditCard,
    UserCog, Bot, KeyRound, Headphones
, Bug
} from 'lucide-react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { HelpButton } from '../HelpButton';

const roleLabels = {
    superadmin: 'Super Admin', support: 'Soporte', account_manager: 'Account Manager',
    billing_admin: 'Finanzas', developer: 'Developer'
};

const adminNavItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['superadmin', 'support', 'account_manager', 'billing_admin', 'developer'] },
    { path: '/admin/tenants', icon: Building2, label: 'Empresas', roles: ['superadmin', 'account_manager', 'support'] },
    { path: '/admin/leads', icon: Target, label: 'Leads & CRM', roles: ['superadmin', 'account_manager'] },
    { path: '/admin/billing', icon: CreditCard, label: 'Facturacion', roles: ['superadmin', 'billing_admin'] },
    { path: '/admin/staff', icon: UserCog, label: 'Equipo', roles: ['superadmin'] },
    { path: '/admin/activity', icon: Activity, label: 'Actividad', roles: ['superadmin', 'developer', 'support'] },
    { path: '/admin/support', icon: Headphones, label: 'Soporte', roles: ['superadmin', 'support'] },
    { path: '/admin/ai', icon: Bot, label: 'Agente IA', roles: ['superadmin', 'support', 'developer'] },
    { path: '/admin/security', icon: KeyRound, label: 'Seguridad', roles: ['superadmin'] },
    { path: '/admin/errors', icon: Bug, label: 'Errores', roles: ['superadmin', 'developer'] },
];

export const AdminLayout = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { admin, logout } = useAdminAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();

    const initials = `${admin?.first_name?.[0] || ''}${admin?.last_name?.[0] || ''}`.toUpperCase();

    const SidebarContent = () => (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="p-4 border-b border-border/50">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-6 h-6 text-amber-500" />
                    </div>
                    {!collapsed && (
                        <div className="animate-fade-in overflow-hidden">
                            <h1 className="text-lg font-bold tracking-tight uppercase font-['Barlow_Condensed']">
                                AeroOps
                            </h1>
                            <p className="text-xs text-amber-500 font-semibold tracking-wider">
                                SUPER ADMIN
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {adminNavItems.filter(item => !item.roles || item.roles.includes(admin?.role)).map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            data-testid={`admin-nav-${item.path.split('/').pop()}`}
                            className={`sidebar-item relative ${isActive ? 'active' : ''}`}
                            onClick={() => setMobileOpen(false)}
                        >
                            <Icon className="w-5 h-5 flex-shrink-0" />
                            {!collapsed && (
                                <span className="animate-fade-in text-sm font-medium">{item.label}</span>
                            )}
                        </NavLink>
                    );
                })}
            </nav>

            {/* Footer */}
            <div className="p-3 border-t border-border/50 space-y-2">
                <button
                    onClick={toggleTheme}
                    data-testid="admin-theme-toggle"
                    className={`sidebar-item w-full ${collapsed ? 'justify-center' : 'justify-start'}`}
                >
                    {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    {!collapsed && <span className="text-sm">Cambiar tema</span>}
                </button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            data-testid="admin-user-menu"
                            className={`sidebar-item w-full ${collapsed ? 'justify-center' : 'justify-start'}`}
                        >
                            <Avatar className="w-8 h-8">
                                <AvatarFallback className="bg-amber-500/10 text-amber-500 text-xs">
                                    {initials}
                                </AvatarFallback>
                            </Avatar>
                            {!collapsed && (
                                <div className="text-left animate-fade-in overflow-hidden">
                                    <p className="text-sm font-medium truncate">{admin?.first_name} {admin?.last_name}</p>
                                    <p className="text-xs text-amber-500">{roleLabels[admin?.role] || admin?.role}</p>
                                </div>
                            )}
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel>{admin?.email}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={logout} data-testid="admin-logout-btn" className="text-destructive focus:text-destructive">
                            <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                <button
                    onClick={() => setCollapsed(!collapsed)}
                    className="sidebar-item w-full justify-center hidden md:flex"
                    data-testid="admin-sidebar-collapse"
                >
                    {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                </button>
            </div>
        </div>
    );

    return (
        <div className="dashboard-layout min-h-screen bg-background">
            {/* Mobile Menu Button */}
            <button
                onClick={() => setMobileOpen(true)}
                className="fixed top-4 left-4 z-50 p-2 rounded-lg bg-card border border-border md:hidden"
                data-testid="admin-mobile-menu"
            >
                <Menu className="w-6 h-6" />
            </button>

            {/* Mobile Overlay */}
            {mobileOpen && (
                <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 md:hidden" onClick={() => setMobileOpen(false)} />
            )}

            {/* Mobile Sidebar */}
            <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-300 md:hidden ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 p-1">
                    <X className="w-5 h-5" />
                </button>
                <SidebarContent />
            </aside>

            {/* Desktop Sidebar */}
            <aside
                className={`hidden md:flex flex-col bg-card border-r border-border transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-[260px]'}`}
                data-testid="admin-sidebar"
            >
                <SidebarContent />
            </aside>

            <main className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="p-4 md:p-6 lg:p-8 pt-16 md:pt-6">
                    {children}
                </div>
            </main>
            <HelpButton />
        </div>
    );
};
